import { useState } from 'react';
import { App, Button, Divider, Input, Select, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { CardAudience } from '@crm/shared';
import { errorMessage } from '../../api/client';
import { useCreateEventTag, useEventTags } from '../../features/cardScans/api';

/** Value of the "I don't remember" choice; the API gets null. */
export const NO_EVENT = '';

const STORAGE_KEY = 'crm.cardScan.eventTagId';

/** The event picked on this device last, so a pile from the same event keeps its tag between visits. */
export function rememberedEvent(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) ?? NO_EVENT;
  } catch {
    return NO_EVENT;
  }
}
export function rememberEvent(id: string) {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // private window: the choice lasts for this visit only
  }
}

const AUDIENCE_KEY = 'crm.cardScan.audience';
const NO_AUDIENCE: CardAudience = { tagIds: [], listIds: [], segmentIds: [] };

/** The tags, lists and segments picked for new cards on this device last. */
export function rememberedAudience(): CardAudience {
  try {
    const v = JSON.parse(localStorage.getItem(AUDIENCE_KEY) ?? 'null') as Partial<CardAudience> | null;
    const ids = (x: unknown) => (Array.isArray(x) ? x.filter((i): i is string => typeof i === 'string') : []);
    return v ? { tagIds: ids(v.tagIds), listIds: ids(v.listIds), segmentIds: ids(v.segmentIds) } : NO_AUDIENCE;
  } catch {
    return NO_AUDIENCE;
  }
}
export function rememberAudience(a: CardAudience) {
  try {
    localStorage.setItem(AUDIENCE_KEY, JSON.stringify(a));
  } catch {
    // private window: the choice lasts for this visit only
  }
}

/**
 * Where the cards were collected: a "Met at" tag, a new one, or "I don't remember" (no tag). Tags from other places
 * can still be chosen when one is already set on a card.
 */
export function EventPicker({ value, onChange, extra, size = 'large' }: { value: string; onChange: (id: string) => void; extra?: { id: string; name: string } | null; size?: 'large' | 'middle' }) {
  const { message } = App.useApp();
  const tags = useEventTags();
  const create = useCreateEventTag();
  const [name, setName] = useState('');
  const add = () => {
    if (!name.trim()) return;
    create
      .mutateAsync(name.trim())
      .then((t) => {
        setName('');
        onChange(t.id);
      })
      .catch((e) => message.error(errorMessage(e)));
  };
  const options = [
    { value: NO_EVENT, label: "I don't remember / no event" },
    ...(extra && !tags.data?.some((t) => t.id === extra.id) ? [{ value: extra.id, label: extra.name }] : []),
    ...(tags.data ?? []).map((t) => ({ value: t.id, label: t.name })),
  ];
  return (
    <Select
      aria-label="Where you met them"
      size={size}
      value={tags.isLoading && value ? undefined : value}
      onChange={onChange}
      loading={tags.isLoading}
      options={options}
      showSearch
      optionFilterProp="label"
      style={{ width: '100%' }}
      popupRender={(menu) => (
        <>
          {menu}
          <Divider style={{ margin: '8px 0' }} />
          <Space.Compact style={{ width: '100%', padding: '0 8px 4px' }}>
            <Input placeholder="New event, e.g. TNS9 Limassol" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.stopPropagation()} onPressEnter={add} />
            <Button type="primary" icon={<PlusOutlined />} loading={create.isPending} disabled={!name.trim()} onClick={add}>
              Add
            </Button>
          </Space.Compact>
        </>
      )}
    />
  );
}
